import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router';
import { CommunityDto } from '@api/ChallengeClient';
import { Button } from '@components/ui/button';
import { CommunityCreateModal } from '@src/communities/community/modal/community-create-modal';
import { TenantFeaturesContext } from '..';
import { FormSection } from '../../components/form-section';
import { Spinner } from '../../components/spinner';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';
import { useError } from '../../components/hooks/useError';

const TenantCommunity = observer(() => {
    const tenantFeatures = React.useContext(TenantFeaturesContext);
    const { challengeClient } = injectTSDI(Api);
    const { tenantName } = useParams();

    const [, setState] = React.useState<string | Error>();
    const showError = useError();

    const [communityDetails, setCommunityDetails] =
        React.useState<CommunityDto | null>(null);
    const [notCreated, setCommunityCreateView] = React.useState<boolean>(false);

    const openActivateState = React.useState(false);
    const [, setCreateModalOpen] = openActivateState;

    const getCommunityDetails = async () => {
        try {
            const response =
                await challengeClient.support.communitySupportControllerGet(
                    tenantName
                );
            setCommunityDetails(response.data);
            setCommunityCreateView(false);
        } catch (e: any) {
            if (e.status === 404) {
                setCommunityCreateView(true);
            } else {
                setState(e);
                showError(e);
            }
        }
    };

    React.useEffect(() => {
        getCommunityDetails();
    }, []);

    return (
        <>
            {tenantFeatures.match({
                loading: () => (
                    <div className="flex min-h-[400px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                fetched: ({ company, tenant }) => (
                    <>
                        <CommunityCreateModal
                            openState={openActivateState}
                            tenantName={tenantName}
                            communityName={company.name}
                            logoBucket={tenant.company.logo?.bucketName}
                            logoKey={tenant.company.logo?.objectKey}
                            onSuccess={() => void getCommunityDetails()}
                        />

                        {notCreated ? (
                            <FormSection
                                title="Community"
                                description="This tenant has no community yet"
                            >
                                <Button
                                    onClick={() =>
                                        void setCreateModalOpen(true)
                                    }
                                >
                                    Create community
                                </Button>
                            </FormSection>
                        ) : (
                            communityDetails && (
                                <FormSection title="Community">
                                    <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
                                        <dt className="text-muted-foreground">
                                            Name
                                        </dt>
                                        <dd>{communityDetails.name}</dd>
                                        <dt className="text-muted-foreground">
                                            Description
                                        </dt>
                                        <dd>{communityDetails.description}</dd>
                                        <dt className="text-muted-foreground">
                                            Visibility
                                        </dt>
                                        <dd>{communityDetails.visibility}</dd>
                                        <dt className="text-muted-foreground">
                                            Approved
                                        </dt>
                                        <dd>
                                            {communityDetails.approved.toString()}
                                        </dd>
                                    </dl>
                                </FormSection>
                            )
                        )}
                    </>
                )
            })}
        </>
    );
});

export default TenantCommunity;
